import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Mail, TestTube2, Zap, CheckCircle2, XCircle, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Copy, Link2, CalendarClock, ShieldCheck, Download, Loader2, PackageSearch, RefreshCw, Search, ChevronDown, ChevronRight, Activity, RotateCcw } from "lucide-react";
import GenerateMonthlyModal from "@/components/GenerateMonthlyModal";
import EmailDeliveryLog from "@/components/EmailDeliveryLog";
import StageEmailAutomations from "@/components/StageEmailAutomations";
import CronHealthPanel from "@/components/CronHealthPanel";
import WholesaleGateDropsPanel from "@/components/WholesaleGateDropsPanel";
import SyncroWebhookHealthPanel from "@/components/SyncroWebhookHealthPanel";

export default function AdminSettings() {
  const [tenant, setTenant] = useState(null);
  const [template, setTemplate] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [graph, setGraph] = useState(null);
  const [graphSavedAt, setGraphSavedAt] = useState(0);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState({});
  const [showGenerate, setShowGenerate] = useState(false);
  // Which tab is visible is driven by the URL (``?tab=email``, etc.) so
  // the sidebar sub-nav entries deep-link straight to a tab and the
  // browser Back button walks between them. ``business`` is the default
  // when no ``tab`` param is present.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["business", "email", "webhooks", "security", "danger"];
  const rawTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : "business";
  const setActiveTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "business") next.delete("tab");
    else next.set("tab", t);
    setSearchParams(next, { replace: false });
  };

  async function reload() {
    try {
      const [t, tpl, g, c] = await Promise.all([
        api.get("/commissions/tenant"),
        api.get("/commissions/template"),
        api.get("/commissions/graph-config").catch(() => ({ data: {} })),
        api.get("/commissions/config"),
      ]);
      setTenant(t.data);
      setTemplate(tpl.data.template);
      setPlaceholders(tpl.data.placeholders);
      setGraph(g.data || {});
      setConfig(c.data);
    } catch (_) {
      toast.error("Failed to load settings");
    }
  }
  useEffect(() => { reload(); }, []);

  async function saveTenant() {
    setBusy((x) => ({ ...x, tenant: true }));
    try {
      await api.patch("/commissions/tenant", {
        name: tenant.name, brand_name: tenant.brand_name,
        gst_pct: tenant.gst_pct, default_rate_pct: tenant.default_rate_pct,
        abn: tenant.abn, logo_url: tenant.logo_url,
        syncro_line_items_mode: tenant.syncro_line_items_mode || "ex_gst",
        auto_payout_enabled: !!tenant.auto_payout_enabled,
        wholesale_category_name: tenant.wholesale_category_name || "Wholesale",
        wholesale_gate_enforced: !!tenant.wholesale_gate_enforced,
        demo_mode: !!tenant.demo_mode,
        support_email: tenant.support_email || "",
        support_phone: tenant.support_phone || "",
        static_ip_addon_enabled: !!tenant.static_ip_addon_enabled,
        static_ip_addon_price: Number(tenant.static_ip_addon_price) || 5.0,
        static_ip_syncro_upc_code: (tenant.static_ip_syncro_upc_code || "").trim(),
      });
      toast.success(tenant.demo_mode ? "Business settings saved · Demo mode ON" : "Business settings saved");
      reload();
      // Ping AuthContext so both shells pick up demo_mode change without a full reload
      window.dispatchEvent(new Event("focus"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy((x) => ({ ...x, tenant: false })); }
  }

  async function saveTemplate() {
    setBusy((x) => ({ ...x, tpl: true }));
    try {
      await api.put("/commissions/template", { pdf: template.pdf, email: template.email });
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy((x) => ({ ...x, tpl: false })); }
  }

  async function saveGraph() {
    setBusy((x) => ({ ...x, graph: true }));
    try {
      await api.put("/commissions/graph-config", {
        entra_tenant_id: graph.entra_tenant_id,
        client_id: graph.client_id,
        client_secret: graph.client_secret,
        sender: graph.sender,
        from_name: graph.from_name,
        inbound_mailbox: graph.inbound_mailbox,
        inbound_enabled: graph.inbound_enabled,
      });
      toast.success("Microsoft Graph settings saved");
      // Flash the inline save-confirm pill for 2s so a silent-fail bug
      // (click goes nowhere) is impossible to miss — if you don't see
      // the green pulse next to the button, the save never fired.
      setGraphSavedAt(Date.now());
      setTimeout(() => setGraphSavedAt((v) => (Date.now() - v > 1800 ? 0 : v)), 2200);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Graph save failed");
    } finally { setBusy((x) => ({ ...x, graph: false })); }
  }

  async function testGraph() {
    const to = (graph.test_to || "").trim();
    if (!to) { toast.error("Enter a recipient email to test."); return; }
    if (!graph.configured) { toast.error("Save Graph settings before testing."); return; }
    setBusy((x) => ({ ...x, graphtest: true }));
    try {
      const { data } = await api.post("/commissions/graph-config/test", { to });
      if (data.demo) toast.info(`Demo mode — would have emailed ${to}. Turn demo off to send for real.`, { duration: 8000 });
      else {
        const rt = (data.reply_to || []).join(", ");
        toast.success(
          `Graph test email sent to ${to}` +
          (rt ? ` · Reply-To: ${rt}` : " · (no Reply-To stamped — inbound mailbox not enabled)"),
          { duration: 8000 }
        );
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Graph test failed");
    } finally { setBusy((x) => ({ ...x, graphtest: false })); }
  }

  if (!tenant || !template || !graph || !config) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-10">
      <header>
        <div className="text-xs uppercase tracking-widest text-cyan-500 font-semibold mb-3">Settings</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium">Configuration</h1>
        <p className="text-slate-400 mt-2">Business identity, PDF &amp; email templates, Microsoft Graph delivery, and inbound webhook diagnostics.</p>
      </header>

      <div hidden={activeTab !== "business"} className="space-y-10">
      {/* Tenant */}
      <Section title="Business" subtitle="Brand and default rates used across all reports.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Business name">
            <input data-testid="tenant-name" value={tenant.name || ""} onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
              className={inputCls} />
          </Field>
          <Field label="Brand name (shown in reports)">
            <input data-testid="tenant-brand" value={tenant.brand_name || ""} onChange={(e) => setTenant({ ...tenant, brand_name: e.target.value })}
              className={inputCls} />
          </Field>
          <Field label="GST %">
            <input data-testid="tenant-gst" type="number" step="0.1" value={tenant.gst_pct || 0} onChange={(e) => setTenant({ ...tenant, gst_pct: Number(e.target.value) })}
              className={inputCls + " font-mono"} />
          </Field>
          <Field label="Default partner rate %">
            <input data-testid="tenant-rate" type="number" step="0.1" value={tenant.default_rate_pct || 0} onChange={(e) => setTenant({ ...tenant, default_rate_pct: Number(e.target.value) })}
              className={inputCls + " font-mono"} />
          </Field>
          <Field label="ABN (for PDF)">
            <input data-testid="tenant-abn" value={tenant.abn || ""} onChange={(e) => setTenant({ ...tenant, abn: e.target.value })}
              className={inputCls + " font-mono"} />
          </Field>
          <Field label="Logo URL (optional)">
            <input data-testid="tenant-logo" value={tenant.logo_url || ""} onChange={(e) => setTenant({ ...tenant, logo_url: e.target.value })}
              className={inputCls} placeholder="https://..." />
          </Field>
          {/* Public support contact — surfaced on stage automation
              emails (e.g. the live welcome card) so customers can
              reach the wholesale provider direct for billing + infra.
              Both fields optional; blank renders as blank in the
              email templates rather than shouting "None". */}
          <Field label="Support email (public — shown on customer emails)">
            <input data-testid="tenant-support-email" type="email"
              value={tenant.support_email || ""}
              onChange={(e) => setTenant({ ...tenant, support_email: e.target.value })}
              className={inputCls} placeholder="support@yourdomain.com" />
          </Field>
          <Field label="Support phone (public — shown on customer emails)">
            <input data-testid="tenant-support-phone"
              value={tenant.support_phone || ""}
              onChange={(e) => setTenant({ ...tenant, support_phone: e.target.value })}
              className={inputCls + " font-mono"} placeholder="1300 000 000" />
          </Field>
          {/* Static IP addon — one flat monthly upsell applied across
              every plan (transfer + new). Off by default; when on,
              the signup wizard renders an opt-in checkbox on the
              Sign & submit step and the partner price list shows a
              "+$X/mo on any plan" banner at the top. */}
          <Field label="Static IP addon — enabled">
            <label className="flex items-center gap-2 mt-1">
              <input
                data-testid="tenant-static-ip-enabled"
                type="checkbox"
                checked={!!tenant.static_ip_addon_enabled}
                onChange={(e) => setTenant({ ...tenant, static_ip_addon_enabled: e.target.checked })}
              />
              <span className="text-sm text-slate-300">
                Show static-IP upsell on wizard + price list
              </span>
            </label>
          </Field>
          <Field label="Static IP addon — monthly price">
            <input data-testid="tenant-static-ip-price"
              type="number" step="0.01" min="0"
              value={tenant.static_ip_addon_price ?? 5}
              onChange={(e) => setTenant({ ...tenant, static_ip_addon_price: e.target.value })}
              className={inputCls + " font-mono"} placeholder="5.00" />
          </Field>
          {/* Optional Syncro SKU that mirrors its price into the addon
              on the 30-minute Syncro price sync cron. Leave blank to
              keep the manually-entered price above sticky. */}
          <Field label="Static IP — Syncro SKU (optional, auto-syncs price)">
            <input data-testid="tenant-static-ip-syncro-upc"
              value={tenant.static_ip_syncro_upc_code || ""}
              onChange={(e) => setTenant({ ...tenant, static_ip_syncro_upc_code: e.target.value })}
              className={inputCls + " font-mono"} placeholder="e.g. STATICIP" />
            <div className="text-xs text-slate-500 mt-1.5">
              When set, the 30-min price-sync cron mirrors this product&apos;s Syncro retail price into the monthly price above. Match the ``upc_code`` on the Syncro product.
            </div>
          </Field>
        </div>

        {/* GST / auto-payout controls */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-slate-800">
          <Field label="Syncro line items are stored as">
            <select
              data-testid="tenant-gst-mode"
              value={tenant.syncro_line_items_mode || "ex_gst"}
              onChange={(e) => setTenant({ ...tenant, syncro_line_items_mode: e.target.value })}
              className={inputCls}
            >
              <option value="ex_gst">Ex-GST (Syncro default — line prices exclude GST)</option>
              <option value="inc_gst">Inc-GST (line prices already include GST — divide by 1+GST%)</option>
            </select>
            <div className="text-xs text-slate-500 mt-1.5">
              Determines how commissions are computed from Syncro line items in both live webhooks and backfills. Ensures consistent commission ledger values.
            </div>
          </Field>
          <Field label="Auto-generate monthly payouts">
            <label className="flex items-center gap-3 mt-2 cursor-pointer">
              <input
                data-testid="tenant-auto-payout"
                type="checkbox"
                checked={!!tenant.auto_payout_enabled}
                onChange={(e) => setTenant({ ...tenant, auto_payout_enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-300">On the 1st of each month, auto-generate pending payouts for every active partner covering the previous month.</span>
            </label>
            <div className="text-xs text-slate-500 mt-1.5">
              Admin still needs to mark each payout as <b>Paid</b> to lock events & auto-email the statement.
            </div>
            <div className="mt-3 flex items-center gap-3 pt-3 border-t border-slate-800/60">
              <button
                data-testid="generate-monthly-now"
                type="button"
                onClick={() => setShowGenerate(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300"
              >
                Preview previous month&apos;s payouts…
              </button>
              <span className="text-xs text-slate-500">
                Opens a dry-run — nothing is created until you confirm. Same job the 1st-of-month scheduler runs.
              </span>
            </div>
          </Field>
          <Field label="Demo mode">
            <label className="flex items-center gap-3 mt-2 cursor-pointer">
              <input
                data-testid="tenant-demo-mode"
                type="checkbox"
                checked={!!tenant.demo_mode}
                onChange={(e) => setTenant({ ...tenant, demo_mode: e.target.checked })}
                className="w-4 h-4 accent-amber-500"
              />
              <span className="text-sm text-slate-300">Enable demo mode — no real emails will be sent and every PDF is watermarked <b className="text-amber-300">DEMO — NOT ISSUED</b>. Perfect for walking your team through the full workflow before going live.</span>
            </label>
            <div className="text-xs text-slate-500 mt-1.5">
              A banner will appear on every page (admin + partner portal) while this is on. Toggle off and save to go live.
            </div>
          </Field>
        </div>

        <div className="mt-6">
          <button data-testid="save-tenant" disabled={busy.tenant} onClick={saveTenant}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Save className="w-4 h-4" /> {busy.tenant ? "Saving…" : "Save business"}
          </button>
        </div>
      </Section>

      {/* Syncro connection */}
      <SyncroSection tenant={tenant} setTenant={setTenant} onSaved={reload} />

      {/* Webhook URLs */}
      <WebhookUrlsSection config={config} />

      {/* Additional event sources (multi-source ingestion) */}
      <EventSourcesSection />

      {/* Wholesale product master */}
      <WholesaleProductsSection config={config} tenant={tenant} setTenant={setTenant} onReload={reload} />

      {/* Ledger integrity + export */}
      <LedgerIntegritySection />

      {/* Commission rules */}
      <RulesSection />

      {/* Partner sign-up guide — admin-editable markdown → branded PDF */}
      <PartnerOnboardingGuideSection />

      {/* Off-site backup — daily Graph push to OneDrive / SharePoint */}
      <OffsiteBackupSection />

      {/* Template */}
      <Section title="Report template"
               subtitle={<>Placeholders: {placeholders.map((p) => <code key={p} className="font-mono text-xs bg-slate-800 px-1.5 py-0.5 rounded mx-1">{`{${p}}`}</code>)}</>}>
        <h4 className="font-medium text-sm mb-3 text-slate-300">PDF</h4>
        <div className="space-y-4">
          <Field label="Header title">
            <input data-testid="tpl-header" value={template.pdf.header_title} onChange={(e) => setTemplate({ ...template, pdf: { ...template.pdf, header_title: e.target.value } })}
              className={inputCls} />
          </Field>
          <Field label="Intro paragraph">
            <textarea data-testid="tpl-intro" rows={2} value={template.pdf.intro} onChange={(e) => setTemplate({ ...template, pdf: { ...template.pdf, intro: e.target.value } })}
              className={inputCls} />
          </Field>
          <Field label="Footer disclaimer">
            <textarea data-testid="tpl-footer" rows={3} value={template.pdf.footer_disclaimer} onChange={(e) => setTemplate({ ...template, pdf: { ...template.pdf, footer_disclaimer: e.target.value } })}
              className={inputCls + " text-sm"} />
          </Field>
        </div>

        <h4 className="font-medium text-sm mt-8 mb-3 text-slate-300">Email</h4>
        <div className="space-y-4">
          <Field label="Subject">
            <input data-testid="tpl-subject" value={template.email.subject} onChange={(e) => setTemplate({ ...template, email: { ...template.email, subject: e.target.value } })}
              className={inputCls} />
          </Field>
          <Field label="Body HTML">
            <textarea data-testid="tpl-body" rows={8} value={template.email.body_html} onChange={(e) => setTemplate({ ...template, email: { ...template.email, body_html: e.target.value } })}
              className={inputCls + " font-mono text-xs"} />
          </Field>
        </div>
        <div className="mt-6">
          <button data-testid="save-template" disabled={busy.tpl} onClick={saveTemplate}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Save className="w-4 h-4" /> {busy.tpl ? "Saving…" : "Save template"}
          </button>
        </div>
      </Section>

      {/* Microsoft Graph (M365) — the only email provider now that SMTP
          was retired in v1.10.62. Uses an Entra ID app registration
          (client-credentials flow, Mail.Send app-permission on a
          scoped shared mailbox). */}
      </div>
      <div hidden={activeTab !== "email"} className="space-y-10">
      <Section title="Microsoft 365 (Graph) delivery"
               subtitle={graph.configured ? "Configured — Entra client-credentials mail is ready." : "Not configured — set this up before any customer-facing send will land."}>
        <p className="text-xs text-slate-500 mb-4 max-w-3xl">
          Send email from Microsoft 365 via an Entra ID app registration (client-credentials flow).
          Scoped to a specific mailbox (e.g. <span className="font-mono text-slate-300">no-reply@yourdomain</span>)
          so the app can&apos;t send from anywhere else in the tenant.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Directory (tenant) ID">
            <input data-testid="graph-tenant-id" value={graph.entra_tenant_id || ""} onChange={(e) => setGraph({ ...graph, entra_tenant_id: e.target.value })} className={inputCls + " font-mono"} placeholder="00000000-0000-0000-0000-000000000000" />
          </Field>
          <Field label="Application (client) ID">
            <input data-testid="graph-client-id" value={graph.client_id || ""} onChange={(e) => setGraph({ ...graph, client_id: e.target.value })} className={inputCls + " font-mono"} placeholder="00000000-0000-0000-0000-000000000000" />
          </Field>
          <Field label="Client secret">
            <input data-testid="graph-client-secret" type="password" value={graph.client_secret || ""} onChange={(e) => setGraph({ ...graph, client_secret: e.target.value })} className={inputCls + " font-mono"} placeholder="••••••••" />
          </Field>
          <Field label="Sender mailbox">
            <input data-testid="graph-sender" type="email" value={graph.sender || ""} onChange={(e) => setGraph({ ...graph, sender: e.target.value })} className={inputCls} placeholder="no-reply@yourdomain.com.au" />
          </Field>
          <Field label="From name">
            <input data-testid="graph-fromname" value={graph.from_name || ""} onChange={(e) => setGraph({ ...graph, from_name: e.target.value })} className={inputCls} placeholder="Steele Cloud" />
          </Field>
        </div>

        {/* Inbound-email — shared mailbox that partners can reply to.
            Requires ``Mail.Read`` app-scope on the same Entra app + a
            shared mailbox (e.g. provision@steelecloud.com.au). Toggle
            off to pause polling without wiping the address. */}
        <div className="mt-6 pt-6 border-t border-slate-800">
          <h4 className="font-medium text-sm text-slate-200 mb-1">Inbound email · reply-by-email</h4>
          <p className="text-xs text-slate-500 mb-3 leading-relaxed max-w-2xl">
            Set a shared mailbox (e.g. <code className="text-slate-300">provision@steelecloud.com.au</code>) and we&apos;ll poll it every 60s. Partner replies to any Kanban <b className="text-slate-300">Email partner</b> note land back on the Syncro ticket as a public comment, and any Cc addresses matching the partner&apos;s domain are auto-added to their CC list. Grant <code className="text-slate-300">Mail.Read</code> application permission on the same Entra app.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Inbound mailbox">
              <input data-testid="graph-inbound-mailbox" type="email" value={graph.inbound_mailbox || ""} onChange={(e) => setGraph({ ...graph, inbound_mailbox: e.target.value })} className={inputCls} placeholder="provision@yourdomain.com.au" />
            </Field>
            <Field label="Enabled">
              <label className="inline-flex items-center gap-2 mt-2 text-sm text-slate-300">
                <input
                  data-testid="graph-inbound-enabled"
                  type="checkbox"
                  checked={!!graph.inbound_enabled}
                  onChange={(e) => setGraph({ ...graph, inbound_enabled: e.target.checked })}
                  className="rounded"
                />
                Poll this mailbox every 60 seconds
              </label>
            </Field>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button data-testid="save-graph" disabled={busy.graph} onClick={saveGraph}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Mail className="w-4 h-4" /> {busy.graph ? "Saving…" : "Save Graph settings"}
          </button>
          {/* Pulse pill: visible for ~2s after every successful save so a
              silent-fail click (wrong form, network drop) is impossible to
              miss. Toast + pill together = belt-and-braces confirm. */}
          {graphSavedAt > 0 && (Date.now() - graphSavedAt) < 2200 && (
            <span
              key={graphSavedAt}
              data-testid="save-graph-confirm-pill"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 text-[11px] font-semibold shadow-[0_0_0_0_rgba(16,185,129,0.5)] animate-[saveFlash_2s_ease-out_forwards]"
              style={{ animation: "saveFlash 2s ease-out forwards" }}
            >
              <CheckCircle2 className="w-3 h-3" /> Saved
            </span>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <TestTube2 className="w-4 h-4 text-cyan-400" />
            <h4 className="font-medium text-sm text-slate-200">Send a Graph test email</h4>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Uses the currently <b className="text-slate-300">saved</b> Graph settings — hit
            <b className="text-slate-300"> Save Graph settings</b> first after any edit.
            The message is a small confirmation via the sender mailbox above.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              data-testid="graph-test-to"
              type="email"
              value={graph.test_to || ""}
              onChange={(e) => setGraph({ ...graph, test_to: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter" && graph.configured) { e.preventDefault(); testGraph(); } }}
              placeholder="you@yourdomain.com"
              className={inputCls + " flex-1 max-w-md"}
            />
            <button
              data-testid="test-graph"
              disabled={busy.graphtest || !graph.configured || !(graph.test_to || "").trim()}
              onClick={testGraph}
              className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 px-4 py-2 rounded-lg text-sm font-semibold shrink-0"
              title={!graph.configured ? "Save your Graph settings first" : ""}
            >
              <TestTube2 className="w-4 h-4" /> {busy.graphtest ? "Sending…" : "Send test"}
            </button>
          </div>
          {!graph.configured && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Graph isn&apos;t saved yet — fill in tenant / client / secret / sender above and hit <b>Save Graph settings</b> to enable the test button.</span>
            </div>
          )}
        </div>
      </Section>

      {/* Email notifications — per-kind on/off toggles */}
      <EmailTogglesSection />

      {/* Delivery log — last 50 sends with re-send button */}
      <EmailDeliveryLog />

      <CronHealthPanel />

      {/* Live webhook health — the "am I fully autonomous?" answer. */}
      <SyncroWebhookHealthPanel />

      {/* Wholesale-gate drops — silently-excluded invoice lines that
          would otherwise leak commission dollars. Sits under CronHealth
          so it's visible on the Email tab where admins already come
          for the "how healthy is the pipeline?" answer. */}
      <WholesaleGateDropsPanel />

      {/* Stage → auto-email rules — dormant by default */}
      <StageEmailAutomations />

      </div>
      <div hidden={activeTab !== "webhooks"} className="space-y-10">
      {/* Webhooks — searchable log of every inbound Syncro webhook, matched or dropped */}
      <WebhookLogsSection />
      </div>
      <div hidden={activeTab !== "security"} className="space-y-10">
      {/* Tenant-wide 2FA policy — nags all users without blocking sign-in */}
      <Force2FAPolicySection tenant={tenant} onSaved={reload} />

      {/* 2FA (admin's own account) */}
      <TwoFactorSection />

      {/* Security */}
      <SecuritySection />

      </div>
      <div hidden={activeTab !== "danger"} className="space-y-10">
      {/* Danger zone */}
      <DangerZone config={config} onLockChange={reload} />
      </div>

      {showGenerate && (
        <GenerateMonthlyModal
          onClose={() => setShowGenerate(false)}
          onGenerated={() => setShowGenerate(false)}
        />
      )}
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20";

function SettingsTabs({ active, onChange }) {
  const tabs = [
    { key: "business", label: "Business" },
    { key: "email", label: "Email" },
    { key: "security", label: "Security" },
    { key: "danger", label: "Danger zone" },
  ];
  return (
    <div
      data-testid="settings-tabs"
      className="sticky top-0 z-20 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-2 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800"
    >
      <div className="flex items-center gap-1 flex-wrap">
        {tabs.map((t) => {
          const on = active === t.key;
          const danger = t.key === "danger";
          const cls = on
            ? (danger
                ? "px-4 py-2 rounded-lg text-sm font-semibold bg-red-500/20 text-red-200 border border-red-500/40"
                : "px-4 py-2 rounded-lg text-sm font-semibold bg-cyan-500/20 text-cyan-200 border border-cyan-500/40")
            : "px-4 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800 border border-transparent";
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`settings-tab-${t.key}`}
              onClick={() => onChange(t.key)}
              className={cls}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-slate-800/80 bg-slate-900 p-6 md:p-8">
      <div className="mb-6">
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        {subtitle && <div className="text-sm text-slate-400 mt-1">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

// ---------- Partner sign-up guide editor ----------
// Admin edits the markdown source; partners download the branded PDF
// from their dashboard. Placeholders like {brand_name}, {partner_code},
// {portal_domain} are substituted at PDF-render time so one template
// serves every partner.
function PartnerOnboardingGuideSection() {
  const [state, setState] = useState(null); // {markdown, updated_at, updated_by, is_default}
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [partnerCode, setPartnerCode] = useState("");
  const [partners, setPartners] = useState([]);

  async function reload() {
    try {
      const [{ data }, plist] = await Promise.all([
        api.get("/commissions/partner-onboarding-guide"),
        api.get("/commissions/partners").catch(() => ({ data: { partners: [] } })),
      ]);
      setState(data);
      setDraft(data.markdown || "");
      setPartners((plist.data.partners || plist.data || []).map(p => ({ code: p.code, name: p.name })));
    } catch (_) { toast.error("Failed to load sign-up guide"); }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/commissions/partner-onboarding-guide", { markdown: draft });
      setState(data);
      toast.success("Sign-up guide saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  async function resetToDefault() {
    if (!window.confirm("Reset the sign-up guide to the default template? Your custom edits will be lost.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/commissions/partner-onboarding-guide/reset");
      setState(data);
      setDraft(data.markdown || "");
      toast.success("Reset to default template");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setResetting(false); }
  }

  function previewPdf() {
    const qs = partnerCode ? `?partner_code=${encodeURIComponent(partnerCode)}` : "";
    window.open(`${API_BASE}/commissions/partner-onboarding-guide.pdf${qs}`, "_blank", "noopener");
  }

  if (!state) return null;
  const dirty = draft !== (state.markdown || "");
  const placeholders = [
    "brand_name", "portal_domain", "partner_code", "partner_name",
    "transfer_upstream", "signup_transfer_url", "signup_new_url", "signup_general_url",
  ];

  return (
    <Section
      title="Partner sign-up guide"
      subtitle="Editable one-pager that partners download from their dashboard. Written in markdown, rendered to a branded PDF with each partner's own code + URLs substituted."
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="uppercase tracking-widest font-semibold">Placeholders</span>
        {placeholders.map(p => (
          <code key={p} className="font-mono bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">{`{${p}}`}</code>
        ))}
      </div>

      <Field label="Markdown source">
        <textarea
          data-testid="partner-guide-markdown"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={22}
          className={inputCls + " font-mono text-xs leading-5"}
          placeholder="# How to sign customers up..."
        />
      </Field>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          data-testid="partner-guide-save"
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>

        <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-2 py-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 px-1">Preview as</span>
          <select
            data-testid="partner-guide-preview-partner"
            value={partnerCode}
            onChange={(e) => setPartnerCode(e.target.value)}
            className="bg-slate-950 border-0 text-sm text-slate-200 outline-none pr-1"
          >
            <option value="">(no partner — raw)</option>
            {partners.map(p => <option key={p.code} value={p.code}>{p.name || p.code}</option>)}
          </select>
          <button
            type="button"
            data-testid="partner-guide-preview-pdf"
            onClick={previewPdf}
            className="inline-flex items-center gap-1 text-sm font-semibold px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100"
          >
            <Download className="w-3.5 h-3.5" /> Preview PDF
          </button>
        </div>

        <button
          data-testid="partner-guide-reset"
          type="button"
          onClick={resetToDefault}
          disabled={resetting || state.is_default}
          title={state.is_default ? "Already on default template" : "Reset to the default template"}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-300 disabled:opacity-50 px-3 py-2 rounded-lg border border-slate-800"
        >
          {resetting ? "Resetting…" : "Reset to default"}
        </button>

        <div className="ml-auto text-[11px] text-slate-500 font-mono">
          {state.is_default
            ? "Using default template"
            : `Last edited ${state.updated_at?.slice(0, 19).replace("T", " ")} by ${state.updated_by || "—"}`}
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500 max-w-3xl leading-relaxed">
        Partners download this as a PDF from their dashboard. Each render substitutes their own <code className="font-mono text-slate-300">partner_code</code>, <code className="font-mono text-slate-300">partner_name</code>, and signup URLs — so one template serves every partner and stays current as your portal changes.
      </p>
    </Section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}


// ---------- Off-site backup (Graph → OneDrive / SharePoint) ----------
// Piggybacks the tenant's existing Microsoft Graph app registration
// (the one already configured for email). Nightly cron pushes the
// latest DB snapshot into a configured folder. Requires the Entra app
// to have ``Files.ReadWrite.All`` (Application) permission granted —
// the "Test push now" button surfaces a 403 with a clear hint when
// it's missing.
function OffsiteBackupSection() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  async function reload() {
    try {
      const { data } = await api.get("/commissions/backups/offsite");
      setCfg(data);
    } catch (_) { toast.error("Failed to load off-site backup config"); }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.put("/commissions/backups/offsite", cfg);
      setCfg(data);
      toast.success("Off-site backup settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  async function test() {
    setTesting(true); setLastTest(null);
    try {
      const { data } = await api.post("/commissions/backups/offsite/test");
      setLastTest(data);
      if (data.ok) {
        toast.success(`Pushed ${data.file} (${Math.round((data.bytes || 0) / 1024)} KB) — retention removed ${data.retention?.deleted || 0}`);
        reload();
      } else {
        toast.error(`Test push failed — ${data.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test push failed");
    } finally { setTesting(false); }
  }

  if (!cfg) return null;
  const isSharepoint = cfg.destination === "sharepoint";
  const lastOk = cfg.last_run_status === "ok";

  return (
    <Section
      title="Off-site backup"
      subtitle="Daily automatic push of the latest DB snapshot to your Microsoft 365 tenant — OneDrive of the sender mailbox, or a SharePoint site. Uses the existing Graph app registration; requires Files.ReadWrite.All (Application) permission on that app with admin consent."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Enable nightly push">
          <label className="flex items-center gap-3 mt-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="offsite-enabled"
              checked={!!cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm text-slate-300">
              Push a fresh snapshot every day at 02:00 AEDT / 01:00 AEST (15:00 UTC).
            </span>
          </label>
        </Field>
        <Field label="Destination">
          <select
            data-testid="offsite-destination"
            value={cfg.destination}
            onChange={(e) => setCfg({ ...cfg, destination: e.target.value })}
            className={inputCls}
          >
            <option value="onedrive">OneDrive of the sender mailbox</option>
            <option value="sharepoint">SharePoint site (default library)</option>
          </select>
        </Field>
        <Field label="Folder path">
          <input
            data-testid="offsite-folder"
            value={cfg.folder_path || ""}
            onChange={(e) => setCfg({ ...cfg, folder_path: e.target.value })}
            className={inputCls}
            placeholder="Steele Cloud Backups"
          />
          <div className="text-xs text-slate-500 mt-1.5">Created automatically if it doesn&apos;t exist. Nested paths like <code className="font-mono bg-slate-800 px-1 rounded">Ops/Backups</code> work.</div>
        </Field>
        <Field label="Retention (days)">
          <input
            type="number" min={1} max={365}
            data-testid="offsite-retention"
            value={cfg.retention_days || 30}
            onChange={(e) => setCfg({ ...cfg, retention_days: Number(e.target.value) || 30 })}
            className={inputCls}
          />
          <div className="text-xs text-slate-500 mt-1.5">Files older than this in the folder get pruned on each run. Only touches files named <code className="font-mono bg-slate-800 px-1 rounded">auto-*</code> or <code className="font-mono bg-slate-800 px-1 rounded">steele-cloud-*</code> — other uploads are safe.</div>
        </Field>
        {isSharepoint && (
          <Field label="SharePoint site id">
            <input
              data-testid="offsite-site-id"
              value={cfg.sharepoint_site_id || ""}
              onChange={(e) => setCfg({ ...cfg, sharepoint_site_id: e.target.value })}
              className={inputCls + " font-mono"}
              placeholder="steelecloud.sharepoint.com,00000000-…,11111111-…"
            />
            <div className="text-xs text-slate-500 mt-1.5">Get it from Graph Explorer: <code className="font-mono">GET /sites/steelecloud.sharepoint.com:/sites/Ops</code> — copy the <code className="font-mono">id</code> field.</div>
          </Field>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          data-testid="offsite-save"
          type="button" onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
        </button>
        <button
          data-testid="offsite-test"
          type="button" onClick={test} disabled={testing || !cfg.enabled}
          title={!cfg.enabled ? "Enable + save first" : "Push a snapshot right now to verify Graph permissions + folder path"}
          className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 px-4 py-2 rounded-lg text-sm font-medium"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
          {testing ? "Pushing…" : "Test push now"}
        </button>
        <div className="ml-auto text-[11px] text-slate-500 font-mono">
          {cfg.last_run_at
            ? `Last run ${cfg.last_run_at.slice(0, 19).replace("T", " ")} · ${cfg.last_run_status || "?"}${cfg.last_run_file ? " · " + cfg.last_run_file : ""}`
            : "Never run"}
        </div>
      </div>

      {cfg.last_run_error && !lastOk && (
        <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="offsite-last-error">
          <span className="uppercase tracking-widest font-bold">Last error</span>
          <div className="mt-1 font-mono">{cfg.last_run_error}</div>
        </div>
      )}
      {lastTest?.ok && (
        <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-200" data-testid="offsite-last-test-ok">
          <span className="uppercase tracking-widest font-bold">Test push OK</span>
          <div className="mt-1">
            {lastTest.file} · {Math.round((lastTest.bytes || 0) / 1024)} KB · retention pruned {lastTest.retention?.deleted || 0} old file(s)
          </div>
        </div>
      )}

      {/* ---- Restore from OneDrive / SharePoint ---- */}
      <OffsiteRestorePanel enabled={!!cfg.enabled} />
    </Section>
  );
}

/**
 * One-click recovery panel. Lists the tarballs currently in the Graph
 * destination folder, lets the admin pick one, type the exact
 * filename to confirm, and runs the destructive restore. Reuses the
 * ``save_uploaded_snapshot`` + ``restore_snapshot`` pipeline so the
 * audit trail matches a manual upload-and-restore.
 */
function OffsiteRestorePanel({ enabled }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/backups/offsite/files");
      setFiles(data.files || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to list files");
      setFiles([]);
    } finally { setLoading(false); }
  }

  async function restore() {
    if (!selected) return;
    if (confirm !== selected.name) {
      toast.error("Confirm text doesn't match the filename");
      return;
    }
    if (!window.confirm(
      `This will WIPE the current database and replace every collection with the contents of ${selected.name}. ` +
      `There is no undo. Continue?`
    )) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/commissions/backups/offsite/restore", {
        item_id: selected.id, confirm,
      });
      toast.success(`Restored ${data.source_file} — refresh in 10 seconds`);
      setSelected(null); setConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    } finally { setRestoring(false); }
  }

  return (
    <div className="mt-8 pt-6 border-t border-slate-800" data-testid="offsite-restore-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-slate-100 text-sm font-semibold uppercase tracking-widest">Restore from OneDrive / SharePoint</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            One-click disaster recovery. Picks a tarball straight out of the destination folder, downloads it via Graph, registers it as a local snapshot, then runs the standard destructive restore. The current DB is overwritten — there is no undo.
          </p>
        </div>
        <button
          type="button"
          data-testid="offsite-restore-reload"
          onClick={reload}
          disabled={loading || !enabled}
          title={!enabled ? "Enable + save first" : "List files in the configured folder"}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300 px-2 py-1 rounded border border-slate-800 hover:border-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          {files === null ? "Load files" : "Refresh"}
        </button>
      </div>

      {files !== null && files.length === 0 && (
        <div className="text-xs text-slate-500 italic">No backups found in the destination folder.</div>
      )}

      {files && files.length > 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 divide-y divide-slate-800" data-testid="offsite-restore-file-list">
          {files.slice(0, 15).map(f => (
            <label key={f.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-900 ${selected?.id === f.id ? "bg-cyan-500/10" : ""}`}>
              <input
                type="radio" name="offsite-file"
                data-testid={`offsite-restore-pick-${f.id}`}
                checked={selected?.id === f.id}
                onChange={() => { setSelected(f); setConfirm(""); }}
                className="accent-cyan-500"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-slate-200 truncate">{f.name}</div>
                <div className="text-[11px] text-slate-500">
                  {Math.round((f.size || 0) / 1024)} KB · created {f.created_at?.slice(0, 19).replace("T", " ")}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      {selected && (
        <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/5 p-4">
          <div className="text-xs text-red-200 mb-2">
            <b>Destructive restore.</b> Type the exact filename below to proceed:
            <code className="ml-1 font-mono text-slate-200 bg-slate-900 px-1 rounded">{selected.name}</code>
          </div>
          <input
            type="text"
            data-testid="offsite-restore-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={selected.name}
            className={inputCls + " font-mono text-xs"}
          />
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              data-testid="offsite-restore-go"
              onClick={restore}
              disabled={restoring || confirm !== selected.name}
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {restoring ? "Restoring…" : "Restore now"}
            </button>
            <button
              type="button"
              onClick={() => { setSelected(null); setConfirm(""); }}
              disabled={restoring}
              className="text-xs text-slate-400 hover:text-slate-100 px-3 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- Email notification toggles ----------

function EmailTogglesSection() {
  const [toggles, setToggles] = useState(null);
  const [busy, setBusy] = useState(null); // kind currently saving

  async function reload() {
    try {
      const { data } = await api.get("/commissions/email-toggles");
      setToggles(data.toggles || []);
    } catch (_) {
      toast.error("Failed to load email toggles");
    }
  }
  useEffect(() => { reload(); }, []);

  async function flip(kind, enabled) {
    setBusy(kind);
    // Optimistic update — instant feedback, revert on error
    setToggles((prev) => prev.map((t) => t.id === kind ? { ...t, enabled } : t));
    try {
      await api.put(`/commissions/email-toggles/${kind}`, { enabled });
      toast.success(enabled
        ? `Enabled — ${kind.replace(/_/g, " ")} emails will now send`
        : `Disabled — no more ${kind.replace(/_/g, " ")} emails will be sent`);
      reload();
    } catch (e) {
      // Roll back on failure
      setToggles((prev) => prev.map((t) => t.id === kind ? { ...t, enabled: !enabled } : t));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(null); }
  }

  if (!toggles) return null;

  // Group by category for a scan-friendly layout
  const groups = toggles.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  const audienceStyle = {
    partner:  { bg: "bg-cyan-500/10",   border: "border-cyan-500/30",   text: "text-cyan-300" },
    customer: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-300" },
    user:     { bg: "bg-violet-500/10", border: "border-violet-500/30", text: "text-violet-300" },
  };

  return (
    <Section
      title="Email notifications"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          Fine-grained control over which emails this system sends. Turn any category off to silence just that flow — everything else keeps working normally. Demo mode still overrides all of these (nothing sends while demo is on).
        </span>
      }
    >
      <div className="space-y-6" data-testid="email-toggles-section">
        {Object.entries(groups).map(([category, items]) => (
          <div key={category}>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
              {category} emails
            </div>
            <div className="space-y-2">
              {items.map((t) => {
                const style = audienceStyle[t.audience] || audienceStyle.user;
                return (
                  <div
                    key={t.id}
                    data-testid={`toggle-row-${t.id}`}
                    className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 flex items-start gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-100">{t.label}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider ${style.bg} ${style.border} ${style.text}`}>
                          → {t.audience}
                        </span>
                        {!t.enabled && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-300 text-[10px] font-semibold uppercase tracking-wider">
                            silenced
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                        {t.description}
                      </div>
                      {t.updated_at && (
                        <div className="text-[10px] text-slate-600 mt-2 font-mono">
                          Last changed {new Date(t.updated_at).toLocaleString()}
                          {t.updated_by ? ` by ${t.updated_by}` : ""}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={t.enabled}
                      data-testid={`toggle-btn-${t.id}`}
                      disabled={busy === t.id}
                      onClick={() => flip(t.id, !t.enabled)}
                      className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500/40 disabled:opacity-60 ${
                        t.enabled ? "bg-cyan-500" : "bg-slate-700"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          t.enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}


// ---------- Two-Factor Authentication (per-user, opt-in) ----------

function TwoFactorSection() {
  const [status, setStatus] = useState(null); // { enabled, backup_codes_remaining, activated_at }
  const [setup, setSetup] = useState(null);   // { secret, provisioning_uri, qr_svg, issuer }
  const [activateCode, setActivateCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null); // shown once after activation
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);

  async function reload() {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (_) { /* silently degrade */ }
  }
  useEffect(() => { reload(); }, []);

  async function startSetup() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setBackupCodes(null);
      setActivateCode("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start 2FA setup");
    } finally { setBusy(false); }
  }

  async function activate() {
    if (activateCode.length !== 6) { toast.error("Enter the 6-digit code from your authenticator"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/activate", { code: activateCode });
      setBackupCodes(data.backup_codes || []);
      setSetup(null);
      setActivateCode("");
      toast.success("Two-factor authentication is now on");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Activation failed");
    } finally { setBusy(false); }
  }

  async function disable() {
    if (!disablePw || disableCode.length !== 6) { toast.error("Password and 6-digit code required"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePw, code: disableCode });
      toast.success("Two-factor authentication disabled");
      setShowDisable(false); setDisablePw(""); setDisableCode("");
      setBackupCodes(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Disable failed");
    } finally { setBusy(false); }
  }

  async function regenerate() {
    if (!disablePw || disableCode.length !== 6) { toast.error("Password and 6-digit code required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-backup-codes",
                                        { password: disablePw, code: disableCode });
      setBackupCodes(data.backup_codes || []);
      setShowRegen(false); setDisablePw(""); setDisableCode("");
      toast.success("Fresh backup codes generated — save them somewhere safe");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Regeneration failed");
    } finally { setBusy(false); }
  }

  function copyCodes() {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Backup codes copied to clipboard");
  }

  function downloadCodes() {
    if (!backupCodes) return;
    const blob = new Blob([`Steele Cloud — 2FA backup codes\nGenerated ${new Date().toISOString()}\n\n${backupCodes.join("\n")}\n\nEach code works once. Store somewhere safe.\n`],
                          { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `steele-cloud-2fa-backup-codes-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const enabled = !!status?.enabled;

  return (
    <Section
      title="Two-factor authentication"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          Protects your admin account from a stolen password. Any TOTP app works — Google Authenticator, 1Password, Authy, Bitwarden.
        </span>
      }
    >
      <div data-testid="twofa-section" className="space-y-6">
        {/* Status row */}
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-100">Your account</span>
              {enabled ? (
                <span
                  data-testid="twofa-status-enabled"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-semibold uppercase tracking-wider"
                >
                  <CheckCircle2 className="w-3 h-3" /> Enabled
                </span>
              ) : (
                <span
                  data-testid="twofa-status-disabled"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-300 text-[10px] font-semibold uppercase tracking-wider"
                >
                  <XCircle className="w-3 h-3" /> Off
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">
              {enabled
                ? `Backup codes remaining: ${status?.backup_codes_remaining ?? 0} of 10${status?.activated_at ? ` · Activated ${new Date(status.activated_at).toLocaleDateString()}` : ""}`
                : "Sign-in will still work with just your password until you turn this on."}
            </div>
          </div>
          {!enabled && !setup && (
            <button
              type="button"
              onClick={startSetup}
              disabled={busy}
              data-testid="twofa-enable-btn"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
            >
              {busy ? "Preparing…" : "Enable 2FA"}
            </button>
          )}
          {enabled && !showDisable && !showRegen && (
            <div className="flex flex-col gap-2 shrink-0">
              <button
                type="button"
                onClick={() => { setShowRegen(true); setDisablePw(""); setDisableCode(""); }}
                data-testid="twofa-regen-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold"
              >
                <RefreshCw className="w-3 h-3" /> New backup codes
              </button>
              <button
                type="button"
                onClick={() => { setShowDisable(true); setDisablePw(""); setDisableCode(""); }}
                data-testid="twofa-disable-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs font-semibold"
              >
                <XCircle className="w-3 h-3" /> Turn off
              </button>
            </div>
          )}
        </div>

        {/* Setup step — QR + verify */}
        {setup && (
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-6 space-y-5" data-testid="twofa-setup-panel">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-300 font-semibold mb-1">Step 1 · Scan</div>
              <p className="text-sm text-slate-300">
                Open your authenticator app and scan this QR code. Then enter the current 6-digit code below to confirm.
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div
                className="bg-white rounded-lg p-3 shrink-0 flex items-center justify-center [&_svg]:w-full [&_svg]:h-full [&_svg]:block"
                data-testid="twofa-qr"
                // qr_svg is server-generated by our 2FA setup endpoint from a
                // known-good SVG QR library, but we still run it through
                // DOMPurify's SVG profile as defence-in-depth so a
                // compromised server-side dep can't inject <script>/on* into
                // the DOM. The child `[&_svg]` selectors force any inline
                // SVG to fill the fixed pixel box even when older cached
                // responses still carry the qrcode library's default
                // physical (mm) dimensions.
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(setup.qr_svg || "", {
                    USE_PROFILES: { svg: true, svgFilters: true },
                  }),
                }}
                style={{ width: 200, height: 200 }}
              />
              <div className="flex-1 min-w-0 space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Can&apos;t scan? Manual key</div>
                  <div className="flex items-center gap-2">
                    <code
                      data-testid="twofa-secret-code"
                      className="flex-1 rounded-md bg-slate-900 border border-slate-800 px-3 py-2 text-sm font-mono text-slate-200 break-all"
                    >
                      {setup.secret}
                    </code>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Secret copied"); }}
                      className="p-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-300"
                      title="Copy secret"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-cyan-300 font-semibold mb-1">Step 2 · Verify</div>
                  <div className="flex gap-2 items-center">
                    <input
                      data-testid="twofa-activate-code"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={activateCode}
                      onChange={(e) => setActivateCode(e.target.value.replace(/\D/g, ""))}
                      className={`${inputCls} font-mono tracking-widest text-center max-w-[180px]`}
                      placeholder="000000"
                    />
                    <button
                      type="button"
                      onClick={activate}
                      disabled={busy || activateCode.length !== 6}
                      data-testid="twofa-activate-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
                    >
                      {busy ? "Verifying…" : "Turn on"}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setSetup(null); setActivateCode(""); }}
                  className="text-xs text-slate-500 hover:text-slate-300"
                >
                  Cancel setup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Backup codes — shown once after activation OR regeneration */}
        {backupCodes && backupCodes.length > 0 && (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-6 space-y-4"
            data-testid="twofa-backup-codes-panel"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-200">Save these codes — they&apos;re shown only once</div>
                <div className="text-sm text-slate-300 mt-1">
                  If you lose your phone, each code below lets you sign in one time. Store them in a password manager.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {backupCodes.map((c) => (
                <code key={c} data-testid={`twofa-backup-code-${c}`} className="text-center font-mono text-sm bg-slate-900 border border-slate-800 rounded px-2 py-2 tracking-widest">
                  {c}
                </code>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyCodes}
                data-testid="twofa-copy-codes"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold"
              >
                <Copy className="w-3.5 h-3.5" /> Copy all
              </button>
              <button
                type="button"
                onClick={downloadCodes}
                data-testid="twofa-download-codes"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold"
              >
                <Download className="w-3.5 h-3.5" /> Download .txt
              </button>
              <button
                type="button"
                onClick={() => setBackupCodes(null)}
                data-testid="twofa-dismiss-codes"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs font-semibold ml-auto"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> I&apos;ve saved them
              </button>
            </div>
          </div>
        )}

        {/* Disable form */}
        {enabled && showDisable && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-6 space-y-4" data-testid="twofa-disable-panel">
            <div className="text-sm text-rose-100">
              To turn 2FA off we need your password plus a live 6-digit code — this prevents a stolen session from disabling it.
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Current password">
                <input
                  type="password"
                  data-testid="twofa-disable-password"
                  value={disablePw}
                  onChange={(e) => setDisablePw(e.target.value)}
                  className={inputCls}
                  autoComplete="current-password"
                />
              </Field>
              <Field label="Current 6-digit code">
                <input
                  data-testid="twofa-disable-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                  className={`${inputCls} font-mono tracking-widest`}
                  placeholder="000000"
                />
              </Field>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={disable}
                disabled={busy}
                data-testid="twofa-confirm-disable"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-500 hover:bg-rose-400 text-slate-50 font-semibold text-sm disabled:opacity-60"
              >
                {busy ? "Disabling…" : "Confirm disable"}
              </button>
              <button
                type="button"
                onClick={() => { setShowDisable(false); setDisablePw(""); setDisableCode(""); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Regenerate backup codes form */}
        {enabled && showRegen && (
          <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-6 space-y-4" data-testid="twofa-regen-panel">
            <div className="text-sm text-slate-300">
              Generate a fresh set of 10 backup codes. Any codes you saved earlier will stop working.
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Current password">
                <input
                  type="password"
                  data-testid="twofa-regen-password"
                  value={disablePw}
                  onChange={(e) => setDisablePw(e.target.value)}
                  className={inputCls}
                  autoComplete="current-password"
                />
              </Field>
              <Field label="Current 6-digit code">
                <input
                  data-testid="twofa-regen-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                  className={`${inputCls} font-mono tracking-widest`}
                  placeholder="000000"
                />
              </Field>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={regenerate}
                disabled={busy}
                data-testid="twofa-confirm-regen"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
              >
                {busy ? "Working…" : "Generate new codes"}
              </button>
              <button
                type="button"
                onClick={() => { setShowRegen(false); setDisablePw(""); setDisableCode(""); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}


// ---------- Force 2FA policy (tenant-wide toggle) ----------

function Force2FAPolicySection({ tenant, onSaved }) {
  const [enabled, setEnabled] = useState(!!tenant?.force_2fa);
  const [busy, setBusy] = useState(false);
  async function save(next) {
    setBusy(true);
    try {
      await api.put("/commissions/tenant/2fa-policy", { force_2fa: next });
      setEnabled(next);
      toast.success(next
        ? "Force-2FA on — every user without 2FA sees the enrol popup on next login"
        : "Force-2FA off — enrol popup will stop appearing");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid="force-2fa-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-slate-100 text-lg font-semibold">Force 2FA for everyone</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            When on, every user — admin and partner — sees a step-by-step enrol popup on their next authenticated page load until they&apos;ve set up an authenticator. Nag-not-a-wall: sign-in still works, so an in-flight task isn&apos;t blocked.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{enabled ? "On" : "Off"}</span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => save(e.target.checked)}
            disabled={busy}
            data-testid="force-2fa-toggle"
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-slate-800 peer-checked:bg-emerald-500 rounded-full relative transition-colors">
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-slate-100 rounded-full transition-transform ${enabled ? "translate-x-4" : ""}`} />
          </div>
        </label>
      </div>
    </section>
  );
}

// ---------- Security (AbuseIPDB + audit log) ----------

function SecuritySection() {
  const [cfg, setCfg] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [allowRaw, setAllowRaw] = useState("");

  async function reload() {
    try {
      const [c, a] = await Promise.all([
        api.get("/commissions/security-config"),
        api.get("/commissions/audit-log?limit=20"),
      ]);
      setCfg(c.data);
      setAllowRaw((c.data.ip_allowlist || []).join("\n"));
      setAudit(a.data.entries || []);
    } catch (_) {
      toast.error("Failed to load security settings");
    }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        abuseipdb_enabled: !!cfg.abuseipdb_enabled,
        abuseipdb_api_key: cfg.abuseipdb_api_key || "",
        abuseipdb_threshold: Number(cfg.abuseipdb_threshold) || 90,
        ip_allowlist: allowRaw.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
        block_action: cfg.block_action || "block",
      };
      await api.put("/commissions/security-config", payload);
      toast.success("Security settings saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  if (!cfg) return null;

  const keyMask = "\u2022".repeat(8);

  return (
    <Section
      title="Security · IP reputation"
      subtitle="Block auth-endpoint requests from IPs flagged by AbuseIPDB. Every sensitive admin action is written to an audit log below."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="AbuseIPDB enabled">
          <label className="flex items-center gap-2 text-sm mt-2">
            <input
              data-testid="security-abuseipdb-enabled"
              type="checkbox"
              checked={!!cfg.abuseipdb_enabled}
              onChange={(e) => setCfg({ ...cfg, abuseipdb_enabled: e.target.checked })}
            />
            Check inbound auth requests against AbuseIPDB
          </label>
        </Field>
        <Field label="Block threshold (confidence score, 25–100)">
          <input
            data-testid="security-abuseipdb-threshold"
            type="number" min="25" max="100"
            value={cfg.abuseipdb_threshold ?? 90}
            onChange={(e) => setCfg({ ...cfg, abuseipdb_threshold: e.target.value })}
            className={inputCls + " font-mono"}
          />
        </Field>
        <Field label="AbuseIPDB API key">
          <div className="flex gap-2">
            <input
              data-testid="security-abuseipdb-key"
              type={showKey ? "text" : "password"}
              value={cfg.abuseipdb_api_key || (cfg.abuseipdb_api_key_set ? keyMask : "")}
              onChange={(e) => setCfg({ ...cfg, abuseipdb_api_key: e.target.value })}
              placeholder={cfg.abuseipdb_api_key_set ? keyMask : "Paste your abuseipdb.com API key"}
              className={inputCls + " font-mono"}
            />
            <button
              type="button"
              data-testid="security-abuseipdb-key-toggle"
              onClick={() => setShowKey(v => !v)}
              className="rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-950 px-3 text-sm"
            >
              {showKey ? "Hide" : "Show"}
            </button>
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            Get a free key at <a href="https://www.abuseipdb.com/account/api" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300">abuseipdb.com/account/api</a>.
          </div>
        </Field>
        <Field label="On abusive IP">
          <select
            data-testid="security-abuseipdb-action"
            value={cfg.block_action || "block"}
            onChange={(e) => setCfg({ ...cfg, block_action: e.target.value })}
            className={inputCls}
          >
            <option value="block">Block the request (403)</option>
            <option value="log_only">Log only — allow the request through</option>
          </select>
        </Field>
      </div>
      <div className="mt-4">
        <Field label="IP allowlist (one per line — never blocked)">
          <textarea
            data-testid="security-allowlist"
            rows={3}
            value={allowRaw}
            onChange={(e) => setAllowRaw(e.target.value)}
            className={inputCls + " font-mono"}
            placeholder="203.0.113.10&#10;198.51.100.42"
          />
        </Field>
      </div>
      <div className="mt-6 flex gap-3">
        <button
          data-testid="security-save-button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          {busy ? "Saving…" : "Save security settings"}
        </button>
      </div>

      <div className="mt-8 border-t border-slate-800 pt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg tracking-tight">Recent audit events</h3>
          <button
            type="button"
            data-testid="security-audit-refresh"
            onClick={reload}
            className="text-xs text-slate-400 hover:text-slate-100"
          >
            Refresh
          </button>
        </div>
        <div className="rounded-lg border border-slate-800 overflow-hidden">
          {(audit || []).length === 0 ? (
            <div className="p-4 text-sm text-slate-500" data-testid="security-audit-empty">
              No audit entries yet. Actions like mark-paid, reopen, merge, overrides
              and password resets appear here.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="security-audit-table">
              <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Actor</th>
                  <th className="text-left px-3 py-2">Action</th>
                  <th className="text-left px-3 py-2">Target</th>
                  <th className="text-left px-3 py-2">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {audit.map((row, i) => (
                  <tr key={row.id || `${row.created_at || ""}-${row.action || ""}-${i}`} className="hover:bg-slate-950/40">
                    <td className="px-3 py-2 text-slate-400 font-mono text-xs whitespace-nowrap">
                      {formatAuditTime(row.created_at)}
                    </td>
                    <td className="px-3 py-2 text-slate-200">{row.actor_email || "system"}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded-md bg-slate-800 text-slate-100 px-2 py-0.5 text-xs font-medium">
                        {row.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-400 font-mono text-xs">
                      {row.target_type}
                      {row.target_id ? <span className="text-slate-600">:{String(row.target_id).slice(0, 8)}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-slate-500 font-mono text-xs">{row.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Section>
  );
}

function formatAuditTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return iso; }
}


// ---------- Wholesale product master ----------

function WholesaleProductsSection({ config, tenant, setTenant, onReload }) {
  const [products, setProducts] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  const [showList, setShowList] = useState(false);
  const [gateBusy, setGateBusy] = useState(false);

  async function loadProducts() {
    try {
      const { data } = await api.get("/commissions/syncro/products");
      setProducts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load products");
    }
  }
  useEffect(() => { loadProducts(); }, []);

  async function runSync() {
    setSyncing(true);
    try {
      const { data } = await api.post("/commissions/syncro/sync-products");
      toast.success(`Synced ${data.fetched} product(s) from Syncro category "${data.category}"${data.deactivated > 0 ? ` · ${data.deactivated} deactivated` : ""}`, { duration: 8000 });
      await loadProducts();
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  }

  async function saveCategory() {
    setSavingCat(true);
    try {
      await api.patch("/commissions/tenant", {
        wholesale_category_name: tenant.wholesale_category_name || "Wholesale",
      });
      toast.success("Category saved");
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingCat(false); }
  }

  async function toggleGate(nextValue) {
    setGateBusy(true);
    try {
      await api.patch("/commissions/tenant", { wholesale_gate_enforced: nextValue });
      setTenant((t) => ({ ...t, wholesale_gate_enforced: nextValue }));
      toast.success(nextValue
        ? "Wholesale gate enabled — only products in the category will produce commissions."
        : "Wholesale gate disabled — all line items are eligible (regex-only mode).");
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change gate");
    } finally { setGateBusy(false); }
  }

  const lastSync = config?.wholesale_products_last_sync;
  const productCount = config?.wholesale_products_count ?? (products?.length || 0);
  const gateOn = !!config?.wholesale_gate_enforced;

  return (
    <Section
      title="Wholesale product master"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <PackageSearch className="w-3.5 h-3.5" />
          Local mirror of the Syncro product category that flags what&apos;s commissionable. Refreshed hourly.
        </span>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Syncro product category to sync">
          <div className="flex gap-2">
            <input
              data-testid="wholesale-category-input"
              value={tenant.wholesale_category_name || "Wholesale"}
              onChange={(e) => setTenant({ ...tenant, wholesale_category_name: e.target.value })}
              className={inputCls}
              placeholder="Wholesale"
            />
            <button
              data-testid="save-wholesale-category"
              onClick={saveCategory}
              disabled={savingCat}
              className="shrink-0 inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-slate-100 px-3 py-2 rounded-lg text-sm"
            >
              {savingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
          <div className="text-xs text-slate-500 mt-1.5">
            In Syncro admin → Products → Categories, create this category and assign every commissionable product to it. Case-insensitive match.
          </div>
        </Field>

        <Field label="Wholesale gate">
          <label className="flex items-center gap-3 mt-2 cursor-pointer">
            <input
              data-testid="wholesale-gate-toggle"
              type="checkbox"
              checked={gateOn}
              disabled={gateBusy}
              onChange={(e) => toggleGate(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm text-slate-300">
              Enforce — only line items pointing at a product in this category will produce commissions.
            </span>
          </label>
          <div className="text-xs text-slate-500 mt-1.5">
            When off (default), all line items are scored using regex rules only — same behaviour as before this feature. Turn on once you&apos;ve categorised products in Syncro.
          </div>
        </Field>
      </div>

      {/* Sync status strip */}
      <div className="mt-6 rounded-lg border border-slate-800 bg-slate-950/40 p-4 flex flex-wrap items-center gap-6" data-testid="wholesale-sync-status">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Products synced</div>
          <div className="font-mono text-2xl mt-1">{productCount}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Last sync</div>
          <div className="font-mono text-sm mt-1 text-slate-300">
            {lastSync ? new Date(lastSync).toLocaleString() : "never"}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Cadence</div>
          <div className="font-mono text-sm mt-1 text-slate-300">Hourly (auto) + manual</div>
        </div>
        <div className="flex-1" />
        <div className="flex gap-2">
          <button
            data-testid="wholesale-sync-now"
            onClick={runSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? "Syncing…" : "Sync now"}
          </button>
          {productCount > 0 && (
            <button
              data-testid="wholesale-toggle-list"
              onClick={() => setShowList((v) => !v)}
              className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900 hover:bg-slate-950 px-4 py-2 rounded-lg text-sm"
            >
              {showList ? "Hide" : "Show"} products
            </button>
          )}
        </div>
      </div>

      {showList && products && products.length > 0 && (
        <div className="mt-4 rounded-lg border border-slate-800 overflow-hidden max-h-96 overflow-y-auto" data-testid="wholesale-product-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5">SKU</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-right px-4 py-2.5">Retail (inc)</th>
                <th className="text-left px-4 py-2.5">Syncro ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {products.map((p) => (
                <tr key={p.syncro_product_id} className="hover:bg-slate-900/60">
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">{p.upc_code || "—"}</td>
                  <td className="px-4 py-3 text-slate-100">{p.name}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">
                    ${(p.price_retail || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">#{p.syncro_product_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {productCount === 0 && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-950/20 p-4 text-sm text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">No products in the &quot;{tenant.wholesale_category_name || "Wholesale"}&quot; category yet.</div>
            <div className="text-xs text-amber-200/70 mt-1">
              In Syncro admin → Products, create a category called <span className="font-mono">{tenant.wholesale_category_name || "Wholesale"}</span> and assign your commissionable SKUs to it, then click <b>Sync now</b>.
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}


// ---------- Ledger integrity + export ----------

function FullBackupButton() {
  const [busy, setBusy] = useState(false);
  async function download() {
    if (!window.confirm(
      "Download a full backup of THIS tenant?\n\n" +
      "Includes: business identity, partners, commission events + payouts + " +
      "rules, migration board (Kanban), signup wizard rows, internet plans, " +
      "legal docs, canned responses, handover parsers, Syncro product cache, " +
      "audit log.\n\n" +
      "IMPORTANT: NBN passwords in the backup are ciphertext. To decrypt on " +
      "a restore, you'll need the SAME NBN_CRED_ENCRYPTION_KEY in the target " +
      "environment. Keep them together in a safe place."
    )) return;
    setBusy(true);
    try {
      const { data, headers } = await api.get(
        "/commissions/backup/export",
        { responseType: "blob" });
      // Turn the blob back into a JSON download the browser saves
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `steele-cloud-full-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const totals = headers?.["x-backup-totals"] || "";
      toast.success(`Backup downloaded${totals ? ` — ${totals}` : ""}`,
                     { duration: 8000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backup failed");
    } finally { setBusy(false); }
  }
  return (
    <button
      data-testid="full-backup-btn"
      onClick={download}
      disabled={busy}
      title="Download every business collection for this tenant as a single JSON file — full disaster-recovery snapshot"
      className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-medium"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      {busy ? "Bundling…" : "Full tenant backup (JSON)"}
    </button>
  );
}

function LedgerIntegritySection() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function runCheck() {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get("/commissions/ledger/integrity-check");
      setResult(data);
      if (data.ok) toast.success("Ledger integrity: all 10 checks passed");
      else toast.error(`Ledger integrity: ${data.checks.filter(c => !c.pass).length} check(s) failing`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Integrity check failed");
    } finally { setBusy(false); }
  }

  async function runExport() {
    setExporting(true);
    try {
      const { data } = await api.get("/commissions/ledger/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `ledger-snapshot-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Snapshot downloaded — ${data.counts.events} events, ${data.counts.payouts} payouts`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    } finally { setExporting(false); }
  }

  return (
    <Section
      title="Ledger integrity"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          Read-only. Verify every event and payout is internally consistent, and download a full JSON snapshot for backup.
        </span>
      }
    >
      <div className="flex flex-wrap gap-3">
        <button
          data-testid="ledger-check-btn"
          onClick={runCheck}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {busy ? "Running…" : "Run integrity check"}
        </button>
        <button
          data-testid="ledger-export-btn"
          onClick={runExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900 hover:bg-slate-950 disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-medium"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {exporting ? "Preparing…" : "Download ledger snapshot (JSON)"}
        </button>
        <FullBackupButton />
      </div>

      {result && (
        <div className="mt-6 rounded-lg border border-slate-800 overflow-hidden" data-testid="ledger-check-result">
          <div className={`p-4 flex items-center justify-between ${result.ok ? "bg-cyan-500/10 border-b border-cyan-500/30" : "bg-rose-950/40 border-b border-rose-500/40"}`}>
            <div>
              <div className={`text-xs uppercase tracking-widest font-semibold ${result.ok ? "text-cyan-400" : "text-rose-300"}`}>
                {result.ok ? "All checks passing" : "Integrity issues detected"}
              </div>
              <div className="font-display text-lg mt-0.5">
                {result.totals.events} events · {result.totals.payouts} payouts · {result.totals.partners} partners
              </div>
            </div>
            <div className="text-xs text-slate-400 font-mono">{new Date(result.checked_at).toLocaleString()}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-2.5 w-16">Rule</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="text-center px-4 py-2.5 w-20">Result</th>
                <th className="text-right px-4 py-2.5 w-24">Offenders</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {result.checks.map((c) => (
                <tr key={c.id} className={c.pass ? "" : "bg-rose-950/20"} data-testid={`check-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{c.id}</td>
                  <td className="px-4 py-3 text-slate-200">{c.label}</td>
                  <td className="px-4 py-3 text-center">
                    {c.pass
                      ? <CheckCircle2 className="inline w-4 h-4 text-cyan-500" />
                      : <XCircle className="inline w-4 h-4 text-rose-400" />}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {c.count > 0 ? <span className="text-rose-300">{c.count}</span> : <span className="text-slate-600">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}


// ---------- Webhook URLs ----------

function WebhookUrlsSection({ config }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const rows = [
    { key: "payment_made",    label: "Payment made",     desc: "Fires when a Syncro payment is applied — accrues commission events." },
    { key: "payment_dispute", label: "Payment dispute",  desc: "Fires on a disputed payment — issues a clawback event to reverse the commission." },
    { key: "payment_failed",  label: "Payment failed",   desc: "Fires when a payment attempt fails — logged for audit; no commission change." },
  ];
  async function copy(url, key) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${key} URL`);
    } catch (_) {
      toast.error("Copy failed");
    }
  }
  const urls = config?.webhook_urls || {};
  return (
    <Section
      title="Syncro webhook URLs"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <Link2 className="w-3.5 h-3.5" />
          Paste these into Syncro admin → Webhooks. Each URL is uniquely signed by your tenant secret.
        </span>
      }
    >
      <div className="space-y-3">
        {rows.map(({ key, label, desc }) => {
          const url = `${backend}${urls[key] || ""}`;
          return (
            <div key={key} className="rounded-lg border border-slate-800 bg-slate-950/40 p-4" data-testid={`webhook-row-${key}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">{label}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800/60 text-slate-400 text-[10px] font-mono">POST</span>
                  </div>
                  <div className="font-mono text-xs text-slate-300 mt-2 break-all">{url}</div>
                  <div className="text-xs text-slate-500 mt-2">{desc}</div>
                </div>
                <button
                  data-testid={`copy-webhook-${key}`}
                  onClick={() => copy(url, label)}
                  className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-950 text-sm font-medium transition-colors"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-xs text-slate-500 flex items-start gap-2">
        <CalendarClock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          After pasting into Syncro, the &quot;Recent activity · Syncro webhook feed&quot; on Overview
          shows live events. If nothing arrives, verify Syncro&apos;s Webhook admin page
          shows a green tick against each of these URLs.
        </span>
      </div>
    </Section>
  );
}


// ---------- Syncro ----------

function SyncroSection({ tenant, setTenant, onSaved }) {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  async function refreshStatus() {
    try {
      const { data } = await api.get("/commissions/syncro/status");
      setStatus(data);
    } catch (_e) {
      // status endpoint failure is non-fatal
    }
  }
  useEffect(() => { refreshStatus(); }, [tenant.syncro_subdomain, tenant.syncro_api_token]);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/commissions/tenant", {
        syncro_subdomain: tenant.syncro_subdomain || "",
        syncro_api_token: tenant.syncro_api_token || "",
      });
      toast.success("Syncro connection saved");
      await refreshStatus();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  async function testConnection() {
    setTesting(true);
    try {
      const { data } = await api.get("/commissions/syncro/status");
      setStatus(data);
      if (data.ok) toast.success(`Connected to Syncro (${data.subdomain}.syncromsp.com)`);
      else toast.error(data.message || "Connection failed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed");
    } finally { setTesting(false); }
  }

  return (
    <Section
      title="Syncro connection"
      subtitle={
        status?.configured
          ? (status.ok
              ? <span className="inline-flex items-center gap-1.5 text-cyan-400"><CheckCircle2 className="w-4 h-4" /> Connected to <b>{status.subdomain}.syncromsp.com</b></span>
              : <span className="inline-flex items-center gap-1.5 text-rose-600"><XCircle className="w-4 h-4" /> {status.message || "Not reachable"}</span>)
          : "Not configured — enter your Syncro API subdomain and token. This enables webhook hydration and partner lookup."
      }>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Syncro subdomain (before .syncromsp.com)">
          <input data-testid="syncro-subdomain"
            value={tenant.syncro_subdomain || ""}
            onChange={(e) => setTenant({ ...tenant, syncro_subdomain: e.target.value.trim().toLowerCase() })}
            className={inputCls + " font-mono"} placeholder="st" />
        </Field>
        <Field label="API token">
          <input data-testid="syncro-token" type="password"
            value={tenant.syncro_api_token || ""}
            onChange={(e) => setTenant({ ...tenant, syncro_api_token: e.target.value })}
            className={inputCls + " font-mono"} placeholder="T••••••••••••••••" />
        </Field>
      </div>
      <div className="mt-6 flex gap-3">
        <button data-testid="save-syncro" disabled={saving} onClick={save}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Syncro"}
        </button>
        <button data-testid="test-syncro" disabled={testing || !status?.configured} onClick={testConnection}
          className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900 hover:bg-slate-950 disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-medium">
          <Zap className="w-4 h-4" /> {testing ? "Testing…" : "Test connection"}
        </button>
      </div>
    </Section>
  );
}

// ---------- Rules ----------

const EMPTY_RULE = { label: "", pattern: "", match_field: "description", rate_pct: 10, priority: 100, active: true, partner_code: null };

function RulesSection() {
  const [rules, setRules] = useState([]);
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/commissions/rules"),
        api.get("/commissions/partners"),
      ]);
      setRules(r.data);
      setPartners(p.data);
    } catch (_) { toast.error("Failed to load rules"); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  async function save(rule) {
    try {
      await api.post("/commissions/rules", rule);
      toast.success("Rule saved");
      setEditing(null); reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await api.delete(`/commissions/rules/${id}`);
      toast.success("Rule deleted"); reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  async function move(rule, delta) {
    const newPriority = Math.max(0, (rule.priority || 100) + delta);
    try {
      await api.post("/commissions/rules", { ...rule, priority: newPriority });
      reload();
    } catch (_e) {
      // best-effort re-order — silently ignore
    }
  }

  return (
    <Section
      title="Commission rules (tiered rates)"
      subtitle="Match Syncro invoice line items to a commission rate. Partner-specific rules take priority over global rules. Unmatched lines fall back to the partner's default rate.">
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-400">Showing <b className="text-slate-200">{filterPartner ? partners.find(p => p.code === filterPartner)?.name || filterPartner : "all rules"}</b> · {rules.filter(rl => !filterPartner || rl.partner_code === filterPartner || rl.partner_code == null).length} shown / {rules.length} total</div>
        <select data-testid="rules-filter-partner" value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)}
          className="rounded-lg border border-slate-800 bg-slate-900 text-slate-100 px-3 py-1.5 text-xs outline-none focus:border-cyan-500">
          <option value="">Filter · all partners</option>
          {partners.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
      </div>
      <div className="rounded-lg border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-4 py-2.5">Priority</th>
              <th className="text-left px-4 py-2.5">Scope</th>
              <th className="text-left px-4 py-2.5">Label</th>
              <th className="text-left px-4 py-2.5">Regex pattern</th>
              <th className="text-right px-4 py-2.5">Rate</th>
              <th className="text-center px-4 py-2.5">Active</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800" data-testid="rules-tbody">
            {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Loading…</td></tr>}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-500">No rules yet — add one below.</td></tr>
            )}
            {rules.filter(rl => !filterPartner || rl.partner_code === filterPartner || rl.partner_code == null).map((rl) => {
              const partnerName = rl.partner_code ? (partners.find(p => p.code === rl.partner_code)?.name || rl.partner_code) : null;
              return (
                <tr key={rl.id} className="hover:bg-slate-950/50">
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1">
                      <span className="font-mono">{rl.priority}</span>
                      <button data-testid={`rule-up-${rl.id}`} onClick={() => move(rl, -5)} className="p-1 rounded hover:bg-slate-800" title="Higher priority"><ArrowUp className="w-3 h-3" /></button>
                      <button data-testid={`rule-down-${rl.id}`} onClick={() => move(rl, +5)} className="p-1 rounded hover:bg-slate-800" title="Lower priority"><ArrowDown className="w-3 h-3" /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {partnerName
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-[10px] font-medium">{partnerName}</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800/60 text-slate-400 text-[10px] font-medium">All partners</span>
                    }
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {rl.label}
                    <div className="text-xs text-slate-500 font-mono">{rl.match_field}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">{rl.pattern}</td>
                  <td className="px-4 py-3 text-right font-mono font-medium text-cyan-400">{rl.rate_pct}%</td>
                  <td className="px-4 py-3 text-center">
                    {rl.active ? <CheckCircle2 className="inline w-4 h-4 text-cyan-500" /> : <XCircle className="inline w-4 h-4 text-slate-500" />}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button data-testid={`rule-edit-${rl.id}`} onClick={() => setEditing(rl)} className="text-xs text-cyan-400 hover:text-cyan-300 font-medium">Edit</button>
                    <button data-testid={`rule-delete-${rl.id}`} onClick={() => remove(rl.id)} className="text-xs text-rose-600 hover:text-rose-700 font-medium inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4">
        <button data-testid="add-rule" onClick={() => setEditing({ ...EMPTY_RULE, priority: (rules[rules.length - 1]?.priority || 90) + 10, partner_code: filterPartner || null })}
          className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900 hover:bg-slate-950 px-4 py-2 rounded-lg text-sm font-medium">
          <Plus className="w-4 h-4" /> Add rule
        </button>
      </div>
      {editing && <RuleModal initial={editing} partners={partners} onClose={() => setEditing(null)} onSave={save} />}
    </Section>
  );
}

function RuleModal({ initial, partners = [], onClose, onSave }) {
  const [f, setF] = useState(initial);
  return (
    <div data-testid="rule-modal" className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center">
          <h3 className="font-display text-xl">{f.id ? "Edit rule" : "New rule"}</h3>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-4">
          <Field label="Applies to">
            <select
              data-testid="rule-partner-scope"
              value={f.partner_code || ""}
              onChange={(e) => setF({ ...f, partner_code: e.target.value || null })}
              className={inputCls}
            >
              <option value="">All partners (global rule)</option>
              {partners.map((p) => <option key={p.code} value={p.code}>{p.name} — override for this partner only</option>)}
            </select>
            <div className="text-xs text-slate-500 mt-1">
              Partner-specific rules always win over global rules for that partner.
            </div>
          </Field>
          <Field label="Label (shown in reports)">
            <input data-testid="rule-label" required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className={inputCls} placeholder="Phone / VoIP" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority (lower runs first)">
              <input data-testid="rule-priority" type="number" min="0" value={f.priority} onChange={(e) => setF({ ...f, priority: Number(e.target.value) })} className={inputCls + " font-mono"} />
            </Field>
            <Field label="Rate %">
              <input data-testid="rule-rate" type="number" step="0.1" min="0" max="100" value={f.rate_pct} onChange={(e) => setF({ ...f, rate_pct: Number(e.target.value) })} className={inputCls + " font-mono"} />
            </Field>
          </div>
          <Field label="Match against">
            <select data-testid="rule-match-field" value={f.match_field} onChange={(e) => setF({ ...f, match_field: e.target.value })} className={inputCls}>
              <option value="description">Line description</option>
              <option value="product_name">Product name</option>
              <option value="both">Both</option>
            </select>
          </Field>
          <Field label="Regex pattern (case-insensitive)">
            <input data-testid="rule-pattern" required value={f.pattern} onChange={(e) => setF({ ...f, pattern: e.target.value })} className={inputCls + " font-mono text-sm"} placeholder="\\b(nbn|wisp|internet)\\b" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="rule-active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
            Active
          </label>
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950">Cancel</button>
            <button data-testid="rule-save" type="submit" className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium">
              Save rule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Danger zone ----------

function DangerZone({ config, onLockChange }) {
  const [busy, setBusy] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [open, setOpen] = useState(false);
  const locked = !!config?.purge_locked;

  async function toggleLock(nextState) {
    setUnlocking(true);
    try {
      await api.patch("/commissions/tenant", { purge_locked: nextState });
      toast.success(nextState ? "Purge locked — the danger button is now sealed." : "Purge unlocked — hard guardrail still blocks purge while live Syncro events exist.");
      if (onLockChange) onLockChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change lock state");
    } finally { setUnlocking(false); }
  }

  async function purge() {
    if (confirmText !== "PURGE") {
      toast.error("Type PURGE to confirm");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/purge-test-data");
      toast.success(
        `Cleared: ${data.events_deleted} events · ${data.payouts_deleted} payouts · ${data.webhook_logs_deleted} webhook logs`
      );
      setOpen(false); setConfirmText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Purge failed");
    } finally { setBusy(false); }
  }

  return (
    <section className="rounded-xl border-2 border-dashed border-rose-500/30 bg-rose-950/20 p-6 md:p-8">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h2 className="font-display text-2xl tracking-tight text-rose-200">Danger zone</h2>
          <p className="text-sm text-rose-300/70 mt-1">
            Irreversible actions. Partners, rules, template and Graph settings are preserved.
          </p>
        </div>
      </div>

      {/* Lock status banner */}
      <div
        data-testid="purge-lock-banner"
        className={`rounded-lg p-4 mb-4 flex items-start gap-3 border ${
          locked
            ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-200"
            : "border-slate-700 bg-slate-900 text-slate-300"
        }`}
      >
        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${locked ? "text-cyan-400" : "text-slate-500"}`} />
        <div className="flex-1 text-sm">
          <div className="font-semibold">
            {locked ? "Purge is locked" : "Purge is unlocked"}
          </div>
          <div className={`text-xs mt-1 ${locked ? "text-cyan-300/80" : "text-slate-500"}`}>
            {locked
              ? "The purge button below is admin-sealed. Even if unlocked, a hard guardrail also refuses purge whenever any live Syncro events exist."
              : "Only the hard guardrail (any live Syncro event blocks purge) is active. Admin-lock is off. Recommended: keep locked in production."}
          </div>
        </div>
        <button
          data-testid="toggle-purge-lock"
          disabled={unlocking}
          onClick={() => toggleLock(!locked)}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-800 bg-slate-950 hover:bg-slate-900 text-xs font-medium text-slate-100 disabled:opacity-60"
        >
          {unlocking ? "…" : (locked ? "Unlock" : "Lock now")}
        </button>
      </div>

      {!open ? (
        <button
          data-testid="open-purge"
          disabled={locked}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 border border-rose-500/40 bg-slate-900 hover:bg-rose-950/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-900 text-rose-200 px-4 py-2 rounded-lg text-sm font-medium"
          title={locked ? "Unlock the guardrail above first" : "Wipe all events, payouts, webhook logs for this tenant"}
        >
          <Trash2 className="w-4 h-4" /> Purge all commission events, payouts &amp; webhook logs
        </button>
      ) : (
        <div className="rounded-lg bg-slate-900 border border-rose-500/30 p-4 space-y-3">
          <div className="text-sm text-slate-200">
            This deletes <b>every commission event, every payout, and every webhook log</b> for
            this tenant. Partners, rules, and settings are kept. Type <span className="font-mono font-semibold text-rose-300">PURGE</span> to confirm.
          </div>
          <input
            data-testid="purge-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type PURGE"
            className="w-full rounded-lg border border-rose-500/40 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 font-mono"
          />
          <div className="flex gap-3">
            <button
              data-testid="purge-cancel"
              onClick={() => { setOpen(false); setConfirmText(""); }}
              className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950"
            >
              Cancel
            </button>
            <button
              data-testid="purge-confirm-btn"
              disabled={busy || confirmText !== "PURGE"}
              onClick={purge}
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <Trash2 className="w-4 h-4" /> {busy ? "Purging…" : "Yes, purge everything"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}


// ---------- Event Sources (multi-source ingestion) ----------
function EventSourcesSection() {
  const [sources, setSources] = useState([]);
  const [adapterTypes, setAdapterTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newSource, setNewSource] = useState({ name: "", adapter_type: "generic_webhook" });

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/sources");
      setSources(data.sources || []);
      setAdapterTypes(data.adapter_types || []);
    } catch (_) {
      toast.error("Failed to load sources");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  async function createSource(e) {
    e.preventDefault();
    if (!newSource.name.trim()) { toast.error("Give the source a name"); return; }
    setCreating(true);
    try {
      await api.post("/commissions/sources", newSource);
      toast.success("Source created — copy the webhook URL to your upstream system");
      setNewSource({ name: "", adapter_type: "generic_webhook" });
      reload();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to create source");
    } finally { setCreating(false); }
  }

  async function toggleActive(s) {
    try {
      await api.patch(`/commissions/sources/${s.id}`, { active: !s.active });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  }

  async function removeSource(s) {
    if (!window.confirm(`Delete source "${s.name}"? Any accrued events remain in the ledger.`)) return;
    try {
      await api.delete(`/commissions/sources/${s.id}`);
      toast.success("Source deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  async function copy(url) {
    try {
      const full = window.location.origin + url;
      await navigator.clipboard.writeText(full);
      toast.success("URL copied");
    } catch (_) {
      toast.error("Copy failed");
    }
  }

  const adapterLabel = (t) => ({
    generic_webhook: "Generic webhook",
    voip_yeastar: "Yeastar PBX",
    voip_atomic: "Atom Telecom",
    manual: "Manual",
    syncro: "Syncro",
  }[t] || t);

  return (
    <section className="rounded-xl border border-slate-800/80 bg-slate-900 p-6 md:p-8" data-testid="event-sources-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-2xl">Event sources</h3>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Plug additional systems (VoIP PBX, SIP trunk provider, custom sites) into the commissions module.
            Each source gets its own signed webhook URL — post normalised events to it and they&apos;ll accrue commissions
            through the same rules engine as Syncro. Payload:{" "}
            <code className="font-mono bg-slate-800 px-1.5 py-0.5 rounded text-xs">
              {`{partner_code, external_id, amount_ex_gst, plan_name?, customer_name?, occurred_at?}`}
            </code>
          </p>
        </div>
      </div>

      <form onSubmit={createSource} className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_200px_auto] gap-3 pb-6 border-b border-slate-800">
        <input
          data-testid="new-source-name"
          value={newSource.name}
          onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
          placeholder="e.g. Yeastar PBX — SIP extensions"
          className={inputCls}
        />
        <select
          data-testid="new-source-type"
          value={newSource.adapter_type}
          onChange={(e) => setNewSource({ ...newSource, adapter_type: e.target.value })}
          className={inputCls}
        >
          {adapterTypes.filter((t) => t !== "syncro" && t !== "manual").map((t) => (
            <option key={t} value={t}>{adapterLabel(t)}</option>
          ))}
        </select>
        <button
          data-testid="new-source-submit"
          type="submit"
          disabled={creating}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 disabled:opacity-60 px-5 py-2.5 rounded-lg text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> {creating ? "Adding…" : "Add source"}
        </button>
      </form>

      <div className="mt-6 space-y-3" data-testid="sources-list">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && sources.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-800 p-6 text-sm text-slate-500 text-center">
            No additional sources yet. Add one above and paste its webhook URL into your PBX / SIP trunk portal.
          </div>
        )}
        {sources.map((s) => (
          <div key={s.id} data-testid={`source-${s.id}`} className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-100">{s.name}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-xs">
                    {adapterLabel(s.adapter_type)}
                  </span>
                  {!s.active && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-400 text-xs">
                      Disabled
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 min-w-0 font-mono text-xs text-slate-400 bg-slate-900 border border-slate-800 rounded px-2 py-1.5 truncate">
                    {s.webhook_url}
                  </code>
                  <button
                    data-testid={`copy-source-${s.id}`}
                    onClick={() => copy(s.webhook_url)}
                    title="Copy full URL"
                    className="p-1.5 rounded border border-slate-800 hover:bg-slate-800 text-slate-400"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-2 text-xs text-slate-500 font-mono">
                  {s.event_count || 0} events · last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "never"}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid={`toggle-source-${s.id}`}
                  onClick={() => toggleActive(s)}
                  className="text-xs font-medium text-cyan-400 hover:text-cyan-300"
                >
                  {s.active ? "Disable" : "Enable"}
                </button>
                <button
                  data-testid={`delete-source-${s.id}`}
                  onClick={() => removeSource(s)}
                  title="Delete source"
                  className="text-rose-500 hover:text-rose-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Webhook logs (searchable, paginated) ----------
function WebhookLogsSection() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");   // all | matched | unmatched
  const [event, setEvent] = useState("all");     // all | payment_made | payment_dispute | payment_failed
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);          // full row for expandedId
  const [detailLoading, setDetailLoading] = useState(false);
  const LIMIT = 25;

  async function load(overrides = {}) {
    setLoading(true);
    try {
      const params = {
        q: overrides.q ?? q,
        status: overrides.status ?? status,
        event: overrides.event ?? event,
        limit: LIMIT,
        skip: overrides.skip ?? skip,
      };
      const { data } = await api.get("/commissions/webhook-log/search", { params });
      setRows(data.entries || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load webhook log");
    } finally {
      setLoading(false);
    }
  }

  // Debounce free-text search so the API isn't hammered on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSkip(0);
      load({ q, skip: 0 });
    }, 300);
    return () => clearTimeout(handle);
     
  }, [q]);

  // Immediate reload when filters change.
  useEffect(() => {
    setSkip(0);
    load({ status, event, skip: 0 });
     
  }, [status, event]);

  async function toggleRow(row) {
    if (expandedId === row.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(row.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/commissions/webhook-log/entry/${row.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load detail");
      setExpandedId(null);
    } finally {
      setDetailLoading(false);
    }
  }

  function nextPage() {
    const next = skip + LIMIT;
    if (next >= total) return;
    setSkip(next);
    load({ skip: next });
    setExpandedId(null);
  }
  function prevPage() {
    const next = Math.max(0, skip - LIMIT);
    if (next === skip) return;
    setSkip(next);
    load({ skip: next });
    setExpandedId(null);
  }

  const page = Math.floor(skip / LIMIT) + 1;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Section
      title="Webhook log"
      subtitle="Every inbound Syncro webhook received by this tenant — matched, unmatched, and errored. Search by customer, payment id, invoice id or partner to see exactly why an event did or didn't accrue."
    >
      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xl">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="webhook-log-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, payment id, invoice id, partner…"
            className={inputCls + " pl-9"}
          />
        </div>
        <select
          data-testid="webhook-log-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={inputCls + " md:w-48"}
        >
          <option value="all">All statuses</option>
          <option value="matched">Matched (accrued)</option>
          <option value="unmatched">Unmatched (dropped)</option>
        </select>
        <select
          data-testid="webhook-log-event"
          value={event}
          onChange={(e) => setEvent(e.target.value)}
          className={inputCls + " md:w-52"}
        >
          <option value="all">All event types</option>
          <option value="payment_made">payment_made</option>
          <option value="payment_dispute">payment_dispute</option>
          <option value="payment_failed">payment_failed</option>
        </select>
        <button
          data-testid="webhook-log-reload"
          onClick={() => load()}
          disabled={loading}
          className="inline-flex items-center gap-2 border border-slate-800 bg-slate-950 hover:bg-slate-900 text-slate-200 disabled:opacity-60 px-3 py-2 rounded-lg text-sm"
          title="Reload"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Summary */}
      <div className="mb-3 text-xs text-slate-500">
        Showing {total === 0 ? 0 : skip + 1}–{Math.min(skip + rows.length, total)} of {total}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="min-w-full text-sm" data-testid="webhook-log-table">
          <thead className="bg-slate-950/60 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2.5 w-6"></th>
              <th className="text-left px-3 py-2.5">Received</th>
              <th className="text-left px-3 py-2.5">Event</th>
              <th className="text-left px-3 py-2.5">Customer</th>
              <th className="text-left px-3 py-2.5">Partner</th>
              <th className="text-right px-3 py-2.5">Ex-GST</th>
              <th className="text-right px-3 py-2.5">Commission</th>
              <th className="text-left px-3 py-2.5">Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="text-center px-3 py-8 text-slate-500">
                  No webhook entries match these filters.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const open = expandedId === row.id;
              const accrued = !!row.accrued;
              return (
                <FragmentRow
                  key={row.id}
                  row={row}
                  open={open}
                  accrued={accrued}
                  onToggle={() => toggleRow(row)}
                  detail={open ? detail : null}
                  detailLoading={open && detailLoading}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-slate-500">Page {page} of {pageCount}</div>
        <div className="flex gap-2">
          <button
            data-testid="webhook-log-prev"
            onClick={prevPage}
            disabled={skip === 0 || loading}
            className="px-3 py-1.5 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            ← Prev
          </button>
          <button
            data-testid="webhook-log-next"
            onClick={nextPage}
            disabled={skip + LIMIT >= total || loading}
            className="px-3 py-1.5 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            Next →
          </button>
        </div>
      </div>
    </Section>
  );
}

function FragmentRow({ row, open, accrued, onToggle, detail, detailLoading }) {
  const receivedStr = row.received_at
    ? new Date(row.received_at).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";
  const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer hover:bg-slate-900/60"
        data-testid={`webhook-log-row-${row.id}`}
      >
        <td className="px-3 py-2.5 text-slate-500">
          {open
            ? <ChevronDown className="w-4 h-4" />
            : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{receivedStr}</td>
        <td className="px-3 py-2.5">
          <span className="font-mono text-xs text-slate-300">{row.event || "—"}</span>
        </td>
        <td className="px-3 py-2.5 text-slate-100 truncate max-w-[220px]">{row.customer_name || <span className="text-slate-600">—</span>}</td>
        <td className="px-3 py-2.5">
          {row.partner_name
            ? <span className="text-slate-100">{row.partner_name}</span>
            : row.partner_code
              ? <span className="font-mono text-xs text-slate-400">{row.partner_code}</span>
              : <span className="text-slate-600">—</span>}
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{money(row.amount_ex_gst)}</td>
        <td className="px-3 py-2.5 text-right font-mono text-xs text-cyan-400">{money(row.commission_amount)}</td>
        <td className="px-3 py-2.5">
          {accrued
            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium border border-emerald-500/30">accrued</span>
            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-medium border border-amber-500/30">dropped</span>}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-950/60" data-testid={`webhook-log-detail-${row.id}`}>
          <td colSpan={8} className="px-6 py-5">
            {detailLoading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading full payload…
              </div>
            )}
            {!detailLoading && detail && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Enriched fields</div>
                  <dl className="text-xs space-y-1.5">
                    <DetailRow k="payment_id"   v={detail.payment_id} mono />
                    <DetailRow k="invoice_id"   v={detail.invoice_id} mono />
                    <DetailRow k="partner_code" v={detail.partner_code} mono />
                    <DetailRow k="partner_name" v={detail.partner_name} />
                    <DetailRow k="customer_name" v={detail.customer_name} />
                    <DetailRow k="amount_ex_gst" v={detail.amount_ex_gst != null ? `$${Number(detail.amount_ex_gst).toFixed(2)}` : null} mono />
                    <DetailRow k="commission_amount" v={detail.commission_amount != null ? `$${Number(detail.commission_amount).toFixed(2)}` : null} mono />
                    <DetailRow k="ip" v={detail.ip} mono />
                    <DetailRow k="tenant_id" v={detail.tenant_id ?? "—"} mono />
                  </dl>
                  {detail.filter_note && (
                    <div className="mt-4 p-3 rounded-md bg-slate-900 border border-slate-800">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Filter note</div>
                      <div className="text-xs text-slate-200">{detail.filter_note}</div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Raw Syncro payload</div>
                  <pre className="text-[11px] leading-5 text-slate-300 bg-slate-900 border border-slate-800 rounded-md p-3 max-h-96 overflow-auto font-mono">
{JSON.stringify(detail.payload ?? {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DetailRow({ k, v, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-slate-500 w-36 shrink-0">{k}</dt>
      <dd className={`text-slate-200 break-all ${mono ? "font-mono text-[11px]" : ""}`}>
        {v == null || v === "" ? <span className="text-slate-600">—</span> : String(v)}
      </dd>
    </div>
  );
}


